import {
  BroadcastChannel,
  createLeaderElection
} from 'broadcast-channel';

import i18n from 'common-module/src/lang';
import IDB from './iDB';
import { SYNC_VERSION, WS_URL, DB_NAME, DB_STORE_CONFIG, APP_VERSION } from './config';
import * as Comlink from 'comlink';
import Vue from 'vue';
import Sync from './sync';
import { loadProjects } from '@/api/project';
import { Notification } from 'zk-ui';
import { indexedDBSubject } from '@/utils/indexedDBObserver';
import localeMap from 'common-module/src/lang/localeMap';
const channelName = 'indexedDB';
import { CacheLanguage } from '@zk/framework-vue';
import * as Sentry from '@sentry/vue';

let idbResolve;
Vue.prototype.$iDB = new Promise(r => idbResolve = r);

export default class IDBSync {

  constructor(token, uid, store){
    this.token = token;
    this.userId = uid;
    this.store = store;
    this.orgId = this.store.getters['user/orgId'];
    this.dbName = `${DB_NAME}:${btoa(String(this.userId))}:${btoa(String(this.orgId))}`;
    this.init();
  }

  async getSyncOption(){
    const options = {
      userId: this.userId,
      orgId: this.orgId,
      dbName: this.dbName,
      wsUrl: WS_URL,
      language: localeMap[i18n.locale],
      token: this.token,
      storeConfig: DB_STORE_CONFIG,
      appVersion: APP_VERSION
    };
    const projectList = this.store.state.app.projectList;
    return new Promise((resolve) => {
      if (projectList?.length){
        resolve(Object.assign(options, { projectList }));
        return;
      }
      let unWatch = this.store.watch(state => state.app.projectList, async projectList => {
        if (projectList.length){
          if (unWatch){
            unWatch();
            unWatch = null;
          }
          resolve(Object.assign(options, { projectList }));
        }
      });
    });
  }

  async init(){
    if (!this.isBrowerSupport()) {
      idbResolve(null);
      return;
    }
    if (!this.store.getters['features/isSwitch']('user:indexdb-switch')) {
      idbResolve(null);
      return;
    }
    this.initDB();
    this.initWorker();
    this.initSyncEvent();
    this.initChannel();
    this.initLeader();
    if (this.workerType === 'sharedWorker'){
      this.initSync();
    }
  }

  initWorker(){
    const { $appSharedWorker, $appWorker } = Vue.prototype;
    if ($appSharedWorker){
      this.worker = $appSharedWorker;
      this.workerType = 'sharedWorker';
    } else if ($appWorker){
      this.worker = $appWorker;
      this.workerType = 'worker';
    } else {
      this.worker = null;
      this.workerType = null;
    }
  }

  initSyncEvent(){
    this.syncEvent = {
      setStoreProjectList: (projectList) => {
        this.store.commit('app/SET_PROJECTLIST', projectList);
      },
      getStoreProjectList:  () => {
        return this.store.state.app.projectList;
      },
      setSyncEnd: (isSyncEnd) => {
        const timer = isSyncEnd ? 1000 : 0;
        setTimeout(
          () =>{
            this.channel.postMessage({ type: 'updateSync', value: isSyncEnd });
            this.store.commit('indexedDB/SET_SYNC_END', isSyncEnd);
          }, timer
        );
      },
      loadProjects: async (data) => {
        const res = await loadProjects(data);
        return res || [];
      },
      showNotification: (option) => {
        Notification(option);
      },
      subject(data){
        indexedDBSubject.next(data);
      },
      handleError(error, data){
        const message = error?.message + error?.stack?.substring(0, 300) + JSON.stringify(data).substring(0, 500);
        Sentry.captureException(new Error(message), {
          sampleRate: 0.1
        });
      }
    };
  }

  initChannel() {
    this.channel = new BroadcastChannel(channelName, {
      type: 'localstorage',
      webWorkerSupport: true
    });
    this.channel.postMessage({ type: 'init', value: { userId: this.userId, syncVersion: SYNC_VERSION } });
    this.channel.addEventListener('message', this.handleChannelMessage.bind(this));
  }

  async initLeader() {
    await createLeaderElection(this.channel).awaitLeadership().then(
      async () => {
        console.log('this tab is now leader');
        if (this.workerType !== 'sharedWorker'){
          this.initSync();
        }
      }
    );
  }

  async initDB(){
    try {
      this.db = new IDB(this.dbName, DB_STORE_CONFIG, { clearOfRebuild: true, maxUpdateTime: Infinity });
      await this.db.open();
      idbResolve(this.db);
    } catch (error) {
      console.error(error);
      idbResolve(null);
    }
  }

  handleChannelMessage(data) {
    const { type, value } = data || {};
    if (type === 'init'){
      const { userId, syncVersion } = value || {};
      if (userId !== this.userId || syncVersion !== SYNC_VERSION){
        this.channel.close();
        setTimeout(() => {
          location.reload();
        }, 5000);
      }
    }
    if (type === 'languageChange'){
      if (value && i18n.locale !== value){
        CacheLanguage.loadByGroup(i18n, value);
      }
    }
    if (type === 'updateSync'){
      this.store.commit('indexedDB/SET_SYNC_END', !!value);
    }
  }

  async initSync() {
    const options = await this.getSyncOption();
    if (!this.workerType){
      this.sync = new Sync(options, this.syncEvent);
    } else {
      try {
        await this.worker.initSync(options, Comlink.proxy(this.syncEvent));
        this.sync = this.worker.syncInstance;
      } catch (error) {
        console.error(error);
        this.sync = new Sync(options, this.syncEvent);
      }
    }
    // 监听语言切换
    i18n.on('change', (language) => {
      this.sync.handleLanguageChange(localeMap[language]);
      this.channel.postMessage({ type: 'languageChange', value: language });
    });
  }

  /**
 * 钉钉，飞书等内置浏览器禁用
 */
  isBrowerSupport() {
    const ua = navigator.userAgent.toLowerCase();
    return !['micromessenger', 'weibo', 'qq', 'wxwork', 'dingtalk', 'lark'].some(
      type => ua.indexOf(type) !== -1
    );
  }

  // eslint-disable-next-line spellcheck/spell-checker
  destory(){
    this.channel.removeEventListener('message', this.handleChannelMessage);
  }
}
